import React from "react";
import Modal from "react-modal";

import { useAppDispatch } from "../../../app/hooks";
import { DumpEvent } from "../../../models";
import { updateDumpEvent } from "../dumpEventsSlice";

import "./EditDumpEventModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxWidth: "420px",
    width: "90%",
    padding: "0",
  },
};

interface Props {
  // The event being edited; null = modal closed.
  event: DumpEvent | null;
  onClose: () => void;
  // Called after a successful save so the caller can refresh the list.
  onSaved: () => void;
}

// Manually set price + weight on a dump event via PATCH /dump_events/{id}.
// "Mark as confirmed" (default on) also sets extraction_status=confirmed so the
// receipt drops out of the pending list; the backend stamps receipt_source=manual.
export function EditDumpEventModal({ event, onClose, onSaved }: Props) {
  const dispatch = useAppDispatch();
  const [price, setPrice] = React.useState("");
  const [weight, setWeight] = React.useState("");
  const [confirm, setConfirm] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Prefill from the event each time the modal opens.
  React.useEffect(() => {
    if (!event) return;
    setPrice(event.paidAmount != null ? String(event.paidAmount) : "");
    setWeight(event.weight != null ? String(event.weight) : "");
    setConfirm(true);
    setError(null);
  }, [event]);

  const save = async () => {
    if (!event) return;
    const priceNum = price.trim() === "" ? undefined : Number(price);
    const weightNum = weight.trim() === "" ? undefined : Number(weight);
    if (
      (priceNum !== undefined && (isNaN(priceNum) || priceNum < 0)) ||
      (weightNum !== undefined && (isNaN(weightNum) || weightNum < 0))
    ) {
      setError("Price and weight must be non-negative numbers.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        updateDumpEvent({
          id: event.id,
          update: { paidAmount: priceNum, weight: weightNum, confirm },
        })
      ).unwrap();
      onSaved();
      onClose();
    } catch (e) {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={event !== null}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Edit dump event"
    >
      <div className="edit-dump-event-modal">
        <div className="edit-dump-event-header">
          <h3>Set price &amp; weight</h3>
          <button
            className="edit-dump-event-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="edit-dump-event-body">
          <label className="edit-field">
            <span>Price paid ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="—"
            />
          </label>
          <label className="edit-field">
            <span>Weight</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="—"
            />
          </label>
          <label className="edit-confirm">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
            />
            <span>Mark as confirmed (clears it from the pending list)</span>
          </label>
          {error && <div className="edit-error">{error}</div>}
        </div>
        <div className="edit-dump-event-footer">
          <button className="btn btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-save" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
